import { Instagram, Facebook, MapPin, Mail, Phone, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import dlcLogo from '@/assets/dlc-logo.png';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe';

const Footer = () => {
  const year = new Date().getFullYear();
  const { email, setEmail, submitting, handleSubmit } = useNewsletterSubscribe('footer-main');

  return (
    <footer className="relative overflow-hidden bg-foreground text-background">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-[24rem] w-[24rem] rounded-full bg-primary/10 blur-3xl" />
      {/* Top primary line */}
      <div className="h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

      <div className="relative container mx-auto px-6 pt-20 pb-10">
        {/* Hero strip — oversized brand statement */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <span className="text-xs font-medium tracking-[0.4em] text-background/40 uppercase">
              Demaidi Int'l Limited Co.
            </span>
            <h2 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] mt-3 mb-6">
              Worldwide<br />
              <span className="text-primary">Tastes</span>, Local<br />
              Hands.
            </h2>
            <p className="text-background/60 text-base md:text-lg max-w-md leading-relaxed">
              DLC distributes the world's most-loved food brands across Palestine — from
              everyday refreshments to playful treats.
            </p>
          </div>

          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-background/10 space-y-6">
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-background/40">Newsletter</span>
              <h3 className="font-display text-2xl mt-2 mb-4">Stay in the loop.</h3>
              <form className="flex flex-col sm:flex-row gap-2" onSubmit={handleSubmit}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-background/5 border border-background/15 rounded-full px-5 py-3 text-sm text-background placeholder:text-background/30 focus:outline-none focus:border-primary/60 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="group flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-5 py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? '...' : 'Join'}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm">
              <a href="mailto:info@dlcgroup.ps" className="flex items-center gap-3 text-background/60 hover:text-background transition-colors">
                <Mail className="w-4 h-4 text-primary" /> info@dlcgroup.ps
              </a>
              <div className="flex items-center gap-3 text-background/60">
                <MapPin className="w-4 h-4 text-primary" /> Ramallah · Palestine
              </div>
            </div>
          </div>
        </div>

        {/* Link columns + credit (above separator line) */}
        <div className="pb-6 border-b border-background/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-10">
            <div>
              <h4 className="text-[10px] tracking-[0.3em] uppercase text-background/40 mb-4">Products</h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-background/70 hover:text-primary transition-colors text-sm">G Drink</Link></li>
                <li><Link to="/caprisun" className="text-background/70 hover:text-primary transition-colors text-sm">Capri-Sun</Link></li>
                <li><Link to="/stimorol" className="text-background/70 hover:text-primary transition-colors text-sm">Stimorol</Link></li>
                <li><Link to="/durukan" className="text-background/70 hover:text-primary transition-colors text-sm">Durukan</Link></li>
                <li><span className="text-background/30 text-sm">Gandour <span className="text-[10px] tracking-wider ml-1">SOON</span></span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] tracking-[0.3em] uppercase text-background/40 mb-4">Explore</h4>
              <ul className="space-y-2.5">
                <li><a href="#product" className="text-background/70 hover:text-primary transition-colors text-sm">Product</a></li>
                <li><a href="#ingredients" className="text-background/70 hover:text-primary transition-colors text-sm">Ingredients</a></li>
                <li><a href="#nutrition" className="text-background/70 hover:text-primary transition-colors text-sm">Nutrition</a></li>
                <li><a href="#reviews" className="text-background/70 hover:text-primary transition-colors text-sm">Reviews</a></li>
                <li><a href="#faq" className="text-background/70 hover:text-primary transition-colors text-sm">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] tracking-[0.3em] uppercase text-background/40 mb-4">Company</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about" className="text-background/70 hover:text-primary transition-colors text-sm">About DLC</Link></li>
                <li><Link to="/careers" className="text-background/70 hover:text-primary transition-colors text-sm">Careers</Link></li>
                <li><Link to="/contact" className="text-background/70 hover:text-primary transition-colors text-sm">Contact</Link></li>
                <li><Link to="/press" className="text-background/70 hover:text-primary transition-colors text-sm">Press</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] tracking-[0.3em] uppercase text-background/40 mb-4">Legal</h4>
              <ul className="space-y-2.5">
                <li><Link to="/privacy" className="text-background/70 hover:text-primary transition-colors text-sm">Privacy</Link></li>
                <li><Link to="/terms" className="text-background/70 hover:text-primary transition-colors text-sm">Terms</Link></li>
                <li><Link to="/cookies" className="text-background/70 hover:text-primary transition-colors text-sm">Cookies</Link></li>
              </ul>
            </div>
          </div>

          {/* Website credit — above separator line */}
          <div className="flex justify-end">
            <p className="text-background/40 text-[11px] tracking-wide">
              Website by{' '}
              <a href="https://gloriouslabs.io/" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-primary transition-colors font-medium">
                Glorious
              </a>
            </p>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-4">
            <img src={dlcLogo} alt="DLC" className="h-10 w-auto opacity-90" />
            <div className="w-px h-8 bg-background/20" />
            <div>
              <p className="text-background/60 text-xs">© 2021–{year} DLC · All rights reserved.</p>
              <p className="text-background/30 text-[11px]">dlcgroup.ps</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://instagram.com/dlc.ps"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-11 h-11 rounded-full border border-background/15 flex items-center justify-center hover:border-primary hover:bg-primary/10 transition-all"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4 text-background/70 group-hover:text-primary transition-colors" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=100083289845118"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-11 h-11 rounded-full border border-background/15 flex items-center justify-center hover:border-primary hover:bg-primary/10 transition-all"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4 text-background/70 group-hover:text-primary transition-colors" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
